import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchPackageV2, getDownloadZipUrl } from "../services/galleryServiceV2";
import { PackageDataV2, PhotoV2 } from "../types/v2";
import { Photo, TimePoint } from "../types";
import GalleryComponent from "./Gallery";
import "../styles/EventGallery.css";
import "../styles/Modal.css";
import "../styles/LanguageSelector.css";
import { Language, orderPageTranslations } from "../translations";

// Convert PhotoV2 to Photo for compatibility with existing Gallery component
const convertToLegacyPhoto = (photo: PhotoV2): Photo => ({
  id: photo.id,
  eventId: photo.galleryId,
  url: photo.url,
  thumbnailUrl: photo.thumbnailUrl,
  takenAt: photo.takenAt,
  width: photo.width,
  height: photo.height,
});


const PackagePage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [pkg, setPackage] = useState<PackageDataV2 | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirst, setIsFirst] = useState(true);
  const [isIntroHidden, setIsIntroHidden] = useState(false);
  const [language, setLanguage] = useState<Language | null>(null);
  const [isZipLoading, setIsZipLoading] = useState(false);

  // Use language from package, or default to "pl" if not yet loaded
  const effectiveLanguage = language || "pl";
  const translations = orderPageTranslations[effectiveLanguage];

  const handleDownloadZip = async () => {
    if (!shareId) return;
    setIsZipLoading(true);
    try {
      const url = await getDownloadZipUrl(shareId);
      if (url) {
        window.location.href = url;
      } else {
        alert("Failed to prepare download. Please try again later.");
      }
    } catch (err) {
      console.error("ZIP download error:", err);
      alert("An error occurred while preparing the download.");
    } finally {
      setIsZipLoading(false);
    }
  };

  // Load package data
  useEffect(() => {
    if (!shareId) return;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const packageData = await fetchPackageV2(shareId);

        if (!packageData) {
          setError("Package not found or unavailable");
          return;
        }

        setPackage(packageData);

        // Set language from package if available
        if (packageData.language) {
          setLanguage(packageData.language);
        }
      } catch (err) {
        console.error("Error loading package data:", err);
        setError("Failed to load package");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [shareId]);

  // Use pre-sorted pkg.photos to maintain chronological order
  const photos = useMemo(() => {
    if (!pkg) return [];
    return pkg.photos;
  }, [pkg]);

  // Convert to legacy format for Gallery component
  const legacyPhotos = useMemo(
    () => photos.map(convertToLegacyPhoto),
    [photos],
  );

  // Empty time points (MVP files don't have timestamps)
  const timePoints: TimePoint[] = [];

  if (isLoading) {
    return <div className="loading">{translations.loadingText}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!pkg) {
    return <div className="error">Package not found</div>;
  }

  // Format expiration date
  const expiresAtFormatted = pkg.expiresAt
    ? new Date(pkg.expiresAt).toLocaleDateString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "Europe/Warsaw",
    })
    : null;

  return (
    <div className="event-gallery-page">
      <header className="gallery-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "10px", marginBottom: "1rem" }}>
          <h1 style={{ margin: 0 }}>{pkg.customerName || translations.introTitle}</h1>
          <button
            onClick={handleDownloadZip}
            disabled={isZipLoading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#2c3e50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: isZipLoading ? "wait" : "pointer",
              fontSize: "1rem",
              opacity: isZipLoading ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            {isZipLoading && <span>⏳</span>}
            {translations.downloadButtonText}
          </button>
        </div>
        {isFirst && !isIntroHidden && (
          <section className="intro" style={{ width: "100%" }}>
            <p>{translations.introParagraph1}</p>
            {expiresAtFormatted && (
              <p>
                <span className="emojis">📸</span>{" "}
                {translations.availableUntilText} {expiresAtFormatted}.{" "}
                {translations.pleaseDownloadText}
              </p>
            )}
            <p>{translations.introDownloadIndividualPhotosInstruction}</p>
            <button
              className="close-intro"
              onClick={() => setIsIntroHidden(true)}
            >
              {translations.closeIntroButton}
            </button>
          </section>
        )}
      </header>

      {legacyPhotos.length > 0 && (
        <GalleryComponent
          photos={legacyPhotos}
          timePoints={timePoints}
          isLoading={isLoading}
          showFullDate={false}
          onIsFirstChange={setIsFirst}
          showDownloadButton={true}
          showTimeline={false}
          language={effectiveLanguage}
        />
      )}

      {legacyPhotos.length === 0 && (
        <div className="empty-state">
          <p>No files in this package yet.</p>
        </div>
      )}
    </div>
  );
};

export default PackagePage;
